import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Spin, Tabs, Typography, Button, message } from 'antd'
import { PlusOutlined, RightOutlined } from '@ant-design/icons'
import { adminApi } from '../../api'
import { PageHeader } from '../../components/ui'
import { ModelProviderModal } from './ModelProviderModal'
import './ModelSettings.css'

export type ModelType = 'llm' | 'embedding' | 'multimodal' | 'rerank'

export type ProviderBoardItem = {
  provider_id: string
  label: string
  provider_note: string
  doc_url: string
  configured: boolean
  api_key_set: boolean
  model_name: string
  is_default: boolean
  is_active: boolean
  profile_id: number | null
}

const MODEL_TABS: { key: ModelType; label: string }[] = [
  { key: 'llm', label: '大语言模型' },
  { key: 'embedding', label: '向量模型' },
  { key: 'multimodal', label: '多模态向量' },
  { key: 'rerank', label: '重排序' },
]

const PROVIDER_BRAND: Record<string, { abbr: string; color: string; bg: string }> = {
  openai: { abbr: 'AI', color: '#10a37f', bg: 'linear-gradient(135deg, #12b886 0%, #0d9488 100%)' },
  deepseek: { abbr: 'DS', color: '#4d6bfe', bg: 'linear-gradient(135deg, #6366f1 0%, #4d6bfe 100%)' },
  dashscope: { abbr: '通', color: '#ff6a00', bg: 'linear-gradient(135deg, #ff922b 0%, #f76707 100%)' },
  qwen: { abbr: 'Q', color: '#1668dc', bg: 'linear-gradient(135deg, #4096ff 0%, #0958d9 100%)' },
  zhipu: { abbr: '智', color: '#1a56db', bg: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)' },
  moonshot: { abbr: 'K', color: '#111827', bg: 'linear-gradient(135deg, #374151 0%, #111827 100%)' },
  spark: { abbr: '讯', color: '#0057ff', bg: 'linear-gradient(135deg, #3b82f6 0%, #0057ff 100%)' },
  siliconflow: { abbr: '硅', color: '#6366f1', bg: 'linear-gradient(135deg, #818cf8 0%, #6366f1 100%)' },
  custom: { abbr: '自', color: '#595959', bg: 'linear-gradient(135deg, #8c8c8c 0%, #595959 100%)' },
  local: { abbr: '本', color: '#389e0d', bg: 'linear-gradient(135deg, #73d13d 0%, #389e0d 100%)' },
}

function brandFor(item: ProviderBoardItem) {
  const hit = PROVIDER_BRAND[item.provider_id]
  if (hit) return hit
  return {
    abbr: item.label.trim().slice(0, 1) || '?',
    color: '#1677ff',
    bg: 'linear-gradient(135deg, #69b1ff 0%, #1677ff 100%)',
  }
}

function statusText(row: ProviderBoardItem) {
  if (row.is_default) return row.model_name || '默认厂商'
  if (row.configured) return row.model_name || (row.api_key_set ? '已配置 API Key' : '已启用')
  return '点击配置'
}

function isModelType(v: string | undefined): v is ModelType {
  return MODEL_TABS.some((t) => t.key === v)
}

export function ModelProfileList() {
  const { modelType: rawType } = useParams()
  const navigate = useNavigate()
  const modelType = isModelType(rawType) ? rawType : 'llm'

  const [rows, setRows] = useState<ProviderBoardItem[]>([])
  const [loading, setLoading] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [modalProviderId, setModalProviderId] = useState<string | null>(null)
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('edit')

  const load = useCallback((type: ModelType) => {
    setLoading(true)
    return adminApi
      .get<{ items: ProviderBoardItem[] }>('/admin/model-profiles/board', {
        params: { model_type: type },
      })
      .then((r) => setRows(r.data.items))
      .catch((e) => message.error(e.message))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    if (rawType && !isModelType(rawType)) {
      void navigate('/manage/models/llm', { replace: true })
      return
    }
    void load(modelType)
  }, [modelType, rawType, navigate, load])

  const onTabChange = (key: string) => {
    void navigate(`/manage/models/${key}`)
  }

  const openProvider = (providerId: string) => {
    setModalProviderId(providerId)
    setModalMode('edit')
    setModalOpen(true)
  }

  const openNew = () => {
    setModalProviderId('custom')
    setModalMode('create')
    setModalOpen(true)
  }

  return (
    <div className="model-settings model-settings-list">
      <div className="model-list-head">
        <PageHeader title="模型管理" description="选择厂商平台，配置 API 接入；标记为默认的项会用于系统运行时。" />
        <Button type="primary" icon={<PlusOutlined />} onClick={openNew}>
          新建
        </Button>
      </div>

      <div className="model-settings-panel">
        <Tabs
          activeKey={modelType}
          onChange={onTabChange}
          className="model-settings-tabs"
          items={MODEL_TABS.map((t) => ({ key: t.key, label: t.label }))}
        />

        <Spin spinning={loading}>
          <div className="model-provider-grid">
            {rows.map((row) => {
              const brand = brandFor(row)
              const configured = row.configured
              return (
                <button
                  key={row.provider_id}
                  type="button"
                  className={`model-provider-card${configured ? ' model-provider-card--on' : ''}${row.is_default ? ' model-provider-card--default' : ''}`}
                  onClick={() => openProvider(row.provider_id)}
                >
                  {row.is_default ? <span className="model-provider-card-ribbon">默认</span> : null}
                  <div className="model-provider-card-inner">
                    <div className="model-provider-card-brand" style={{ background: brand.bg }}>
                      {brand.abbr}
                    </div>
                    <div className="model-provider-card-body">
                      <div className="model-provider-card-label">{row.label}</div>
                      <div className={`model-provider-card-status${configured ? ' is-on' : ''}`}>
                        <span className="model-provider-card-dot" />
                        {statusText(row)}
                      </div>
                    </div>
                    <RightOutlined className="model-provider-card-arrow" />
                  </div>
                </button>
              )
            })}
          </div>
          {!loading && rows.length === 0 ? (
            <Typography.Paragraph type="secondary" className="model-provider-empty">
              暂无可用厂商
            </Typography.Paragraph>
          ) : null}
        </Spin>
      </div>

      <ModelProviderModal
        open={modalOpen}
        modelType={modelType}
        providerId={modalProviderId}
        mode={modalMode}
        onClose={() => setModalOpen(false)}
        onSaved={() => void load(modelType)}
      />
    </div>
  )
}
